import {
  B2xPermissionDetail,
  B2xPermissionProducts,
  BusinessTypeValue,
  IS_DANARAPAY,
} from 'src/constants';
import { PATH } from 'src/constants/Path';
import {
  useBusinessTypeData,
  useCheckMAMViewOnlyChildType,
  useDataActivation,
  useGetChildrenList,
  useProductActiveV2,
} from 'src/hooks';
import { useFetchIsParentActiveMultiApproval } from 'src/pages/Settings/MultiApproval/hooks';
import { usePermission } from './permission';
import { useAccountApproved } from './useAccountApproved';
import { useDomainEnv } from './useDomainEnv';
import { useEnv } from './useEnv';
// eslint-disable-next-line import/no-deprecated
import { useSelector } from 'react-redux';
import { whiteListedUsernameDanarapay } from 'src/constants/WhiteListUsername';
import { RootState } from 'src/redux/reducers/rootReducer';
import { useProductActive } from './useProductActive';

export const usePathValidation = () => {
  const isAccountApproved = useAccountApproved();
  const { isProdEnv } = useEnv();
  const { isDomainProdEnv } = useDomainEnv();
  const { hasPermission } = usePermission();
  const { checkActive } = useProductActiveV2();
  const { isViewOnlyChildActive } = useCheckMAMViewOnlyChildType();
  const { isMemTrulyActive } = useGetChildrenList();
  const { data: businessData } = useBusinessTypeData();

  const isNewFlow = checkActive('OVERBOOKING') || checkActive('SOURCE_OF_FUND');

  const username =
    useSelector((state: RootState) => state.auth.user?.username) || '';
  const isWhiteListedShowTabs = whiteListedUsernameDanarapay.includes(username);

  const {
    isIntegrationActive,
    isAccountReceivableDemo,
    isAccountPayableDemo,
    isCashDeliveryActive,
    isCashDeliveryEditor,
    isCreateVaDashboardActive,
    isCashInAlfamartActive,
    isCashInIndomaretActive,
    isSubAccountActive,
    // eslint-disable-next-line import/no-deprecated
  } = useProductActive();
  const { data: childrenList } = useGetChildrenList();
  const { data: isParentApprovalActive } =
    useFetchIsParentActiveMultiApproval();

  const { result } = useDataActivation(false);
  const isSOFActive = result?.get('SOURCE_OF_FUND')?.status === 'ACTIVE';
  /**
   * return true if the pathname contain some of the path
   */
  const findPath = (path: string, pathname: string) => {
    if (pathname.includes(path)) return pathname;

    return '';
  };

  const isShowIfViewOnlyChildActive = (path: string) => {
    // disabled route for MAM if view-only is active
    switch (path) {
      //* EXACT
      case PATH.ACCOUNTS.bankAccount:
      case PATH.announcements:
      case PATH.apiInquiry:
      case PATH.apiTransactionData:
      case PATH.bcaActivation:
      case PATH.SETTINGS.autoWithdrawal:
      case PATH.SETTINGS.developerOption:
      case PATH.SETTINGS.multiApproval:
      case PATH.SETTINGS.offlineChannelPayment:
      case PATH.SETTINGS.transactionCategory:
      case PATH.SETTINGS.bankTransferCallback:
      case PATH.upgrade:
      case PATH.vaActivation:
      case PATH.walletRequest:

      //* COMPOSITE
      // eslint-disable-next-line no-fallthrough
      case findPath(PATH.moreFeatures, path):
      case findPath(PATH.ACCOUNT_PAYABLE.path, path):
      case findPath(PATH.ACCOUNT_PAYABLE.vendorManagement, path):
      case findPath(PATH.ACCOUNT_RECEIVABLE.path, path):
      case findPath(PATH.ACCOUNTS.userManagement, path):
      case findPath(PATH.BRIDGING_REPORT.path, path):
      case findPath(PATH.INTEGRATION.path, path):
      case findPath(PATH.MULTI_ACCOUNT_MANAGEMENT.path, path):
      case findPath(PATH.RECEIVE_MONEY.path, path):
      case findPath(PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.path, path):
      case findPath(PATH.SEND_MONEY.path, path):
      case findPath(PATH.EXPENSE_MANAGEMENT.CORPORATE_CARD.path, path):
      case findPath(PATH.EXPENSE_MANAGEMENT.REIMBURSEMENT.path, path):
        return !isViewOnlyChildActive;
      default:
        return true;
    }
  };

  const isPathAccessibleByPermission = (path: string) => {
    switch (path) {
      case PATH.upgrade:
        return hasPermission(
          B2xPermissionProducts.UPGRADE_ACCOUNT,
          B2xPermissionDetail.VIEWER,
        );
      case PATH.moreFeatures:
        return hasPermission(
          B2xPermissionProducts.ADD_PRODUCT,
          B2xPermissionDetail.VIEWER,
        );

      //* Accounts
      case PATH.ACCOUNTS.bankAccount:
        return hasPermission(
          B2xPermissionProducts.BANK_ACCOUNT,
          B2xPermissionDetail.VIEWER,
        );
      case PATH.ACCOUNTS.userManagementCreate:
      case PATH.ACCOUNTS.userManagementEdit:
        return (
          isProdEnv &&
          hasPermission(
            B2xPermissionProducts.USER_MANAGEMENT,
            B2xPermissionDetail.EDITOR,
          )
        );
      case PATH.ACCOUNTS.userManagement:
        return (
          !isProdEnv ||
          hasPermission(
            B2xPermissionProducts.USER_MANAGEMENT,
            B2xPermissionDetail.VIEWER,
          )
        );
      case PATH.ACCOUNTS.accountActivity:
        return hasPermission(
          B2xPermissionProducts.B2X_USER_ACTIVITY_LOGS,
          B2xPermissionDetail.VIEWER,
        );

      //* Settings
      case PATH.SETTINGS.autoReport:
        // adjust this with config isNewFlow
        return (
          isAccountApproved &&
          hasPermission(
            B2xPermissionProducts.AUTO_REPORT,
            B2xPermissionDetail.VIEWER,
          )
        );
      case PATH.SETTINGS.autoWithdrawal:
        return hasPermission(
          B2xPermissionProducts.AUTO_WITHDRAWAL,
          B2xPermissionDetail.VIEWER,
        );
      case PATH.SETTINGS.transactionCategory:
        return hasPermission(
          B2xPermissionProducts.TRANSACTION_CATEGORY,
          B2xPermissionDetail.VIEWER,
        );
      case PATH.SETTINGS.notification:
        return hasPermission(
          B2xPermissionProducts.EMAIL_NOTIFICATION,
          B2xPermissionDetail.VIEWER,
        );
      case PATH.SETTINGS.developerOption:
        return hasPermission(
          B2xPermissionProducts.DEVELOPER_OPTION,
          B2xPermissionDetail.VIEWER,
        );
      case PATH.SETTINGS.multiApproval:
        return hasPermission(
          B2xPermissionProducts.MULTI_APPROVAL,
          B2xPermissionDetail.VIEWER,
        );
      case PATH.SETTINGS.paymentMethodRequest:
        return hasPermission(
          B2xPermissionProducts.PAYMENT_METHOD_REQUEST,
          B2xPermissionDetail.VIEWER,
        );
      case PATH.RECEIVE_MONEY.PAYMENT_LINK.setting:
        return hasPermission(
          B2xPermissionProducts.PAYMENT_LINK,
          B2xPermissionDetail.EDITOR,
        );
      case PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.create:
        return hasPermission(
          B2xPermissionProducts.INTERNATIONAL_TRANSFER,
          B2xPermissionDetail.MAKER,
        );
      case PATH.SEND_MONEY.CLAIM_FUND.create:
        return hasPermission(
          B2xPermissionProducts.CLAIM_FUND,
          B2xPermissionDetail.MAKER,
        );
      default:
        return true;
    }
  };

  const isIncludeToStaging = (path: string) => {
    const excludedList = [
      PATH.BRIDGING_REPORT.path,
      PATH.BRIDGING_REPORT.pathV2,
      PATH.INTEGRATION.configuration,
      PATH.INTEGRATION.connect,
      PATH.INTEGRATION.path,
      PATH.EXPENSE_MANAGEMENT.BRIDGING_REPORT.path,
      PATH.MULTI_ACCOUNT_MANAGEMENT.childrenStatement,
      PATH.TRANSACTION_REPORT.accountStatement,
      PATH.SETTINGS.autoReport,
    ];
    return !isProdEnv && !excludedList.includes(path);
  };

  /**
   * Don't forget to check useSectionValidation file if your menu didn't show up on sidebar
   * @param path
   */
  const isPathAccessible = (path: string) => {
    switch (path) {
      //* GENERAL
      // - exact --------
      case PATH.apiInquiry:
        return checkActive('API_INQUIRY');
      case PATH.apiTransactionData:
        return checkActive('TRANSACTION_DATA');
      case PATH.upgrade:
        return !isAccountApproved;

      // Setting
      case PATH.SETTINGS.eWalletCallback:
      case PATH.SETTINGS.offlineChannelPayment:
      case PATH.SETTINGS.bankTransferCallback:
      case PATH.SETTINGS.ccDcCallback:
        return !isProdEnv;
      case PATH.SETTINGS.autoReport:
      case PATH.SETTINGS.autoWithdrawal:
      case PATH.SETTINGS.transactionCategory:
      case PATH.SETTINGS.paymentMethodRequest:
        return isAccountApproved;
      case PATH.devConfig:
        return !isDomainProdEnv;
      case PATH.ACCOUNTS.bankAccount:
      case PATH.SETTINGS.multiApproval:
        return !isParentApprovalActive || childrenList.length > 0;
      // - composite --------
      case findPath(PATH.moreFeatures, path):
        return !isProdEnv || isAccountApproved;
      case findPath(PATH.MULTI_ACCOUNT_MANAGEMENT.path, path):
        const isSubEntityStatement =
          path === PATH.MULTI_ACCOUNT_MANAGEMENT.childrenStatement;

        if (isSubEntityStatement) {
          // TODO: change with config newFlow
          return isAccountApproved && !isNewFlow && isMemTrulyActive;
        }
        return isMemTrulyActive;
      case findPath(PATH.INTEGRATION.path, path):
        return isIntegrationActive;
      // - end --------

      //* Send Money
      // - exact --------
      case PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.list:
      case PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.detail:
      case PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.create:
        return (
          checkActive('INTERNATIONAL_TRANSFER') ||
          checkActive('INTERNATIONAL_TRANSFER_API')
        );
      case PATH.SEND_MONEY.apiBiller:
        return checkActive('API_BILLER');
      case PATH.SEND_MONEY.apiDisbursement:
        return checkActive('API_DISBURSE');
      case PATH.SEND_MONEY.CLAIM_FUND.path:
        return checkActive('CLAIM_FUND') || checkActive('CLAIM_FUND_API');
      case PATH.SEND_MONEY.CLAIM_FUND.create:
        return checkActive('CLAIM_FUND');
      // - composite --------
      case findPath(PATH.ACCOUNT_PAYABLE.path, path):
      case findPath(PATH.ACCOUNT_PAYABLE.vendorManagement, path):
        return (
          (checkActive('ACCOUNT_PAYABLE') || isAccountPayableDemo) &&
          !IS_DANARAPAY
        );
      case findPath(PATH.BRIDGING_REPORT.pathV2, path):
        return checkActive('BRIDGED');
      case findPath(PATH.BRIDGING_REPORT.path, path):
        return checkActive('OVERDRAFT');
      case findPath(PATH.SEND_MONEY.bulkDisbursement, path):
        return checkActive('BULK_DISBURSEMENT');
      case findPath(PATH.SEND_MONEY.apiDisbursementDetailPath, path):
        return checkActive('API_DISBURSE');
      case findPath(PATH.SEND_MONEY.INTERNATIONAL_REMITTANNCE.list, path):
        return (
          checkActive('INTERNATIONAL_TRANSFER') ||
          checkActive('INTERNATIONAL_TRANSFER_API')
        );
      // - end --------

      //* Receive Money
      // - exact --------
      case PATH.RECEIVE_MONEY.PAYMENT_LINK.setting:
        return true;
      case PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.createdNewVA:
        return (
          (!IS_DANARAPAY || isWhiteListedShowTabs) && isCreateVaDashboardActive
        );
      // - composite --------
      case findPath(PATH.ACCOUNT_RECEIVABLE.createInvoice, path):
      case findPath(PATH.ACCOUNT_RECEIVABLE.customerManagement, path):
      case findPath(PATH.ACCOUNT_RECEIVABLE.tokenizationDetail, path):
        return checkActive('ACCOUNT_RECEIVABLE') || isAccountReceivableDemo;
      case findPath(PATH.RECEIVE_MONEY.paymentRouting, path):
        return checkActive('PAYMENT_ROUTING');
      case findPath(PATH.RECEIVE_MONEY.PAYMENT_LINK.path, path):
        return !IS_DANARAPAY ? checkActive('PAYMENT_CHECKOUT') : false;
      case findPath(PATH.RECEIVE_MONEY.VIRTUAL_ACCOUNT.path, path):
        return (
          (!IS_DANARAPAY || isWhiteListedShowTabs) &&
          checkActive('API_STATIC_VA')
        );
      case findPath(PATH.RECEIVE_MONEY.eWallet, path):
        return !IS_DANARAPAY ? checkActive('EWALLET_AGGREGATOR') : false;
      case findPath(PATH.RECEIVE_MONEY.qrisStatic, path):
        return !IS_DANARAPAY ? checkActive('QRIS_STATIC_TRANSACTION') : false;
      // - end --------

      //* Cash Management
      // - exact --------
      case PATH.SEND_MONEY.offlineCashWithdrawal:
      case PATH.SEND_MONEY.offlineCashWithdrawalCreate:
        return checkActive('CASH_WITHDRAWAL');
      case PATH.SEND_MONEY.apiOfflineCashOut:
        return checkActive('CASH_WITHDRAWAL_API');
      case PATH.RECEIVE_MONEY.offlineCashIn:
        return checkActive('CASH_DEPOSIT_API');
      case PATH.RECEIVE_MONEY.cashDepositMachine:
        return checkActive('CASH_DEPOSIT_MACHINE');
      case PATH.RECEIVE_MONEY.offlineCashDepositCreate:
        return (
          checkActive('CASH_DEPOSIT') &&
          (isCashInAlfamartActive || isCashInIndomaretActive)
        );
      case PATH.RECEIVE_MONEY.offlineCashDeposit:
        return checkActive('CASH_DEPOSIT');
      case PATH.RECEIVE_MONEY.cashDeliveryCreate:
        return isCashDeliveryEditor;
      case PATH.RECEIVE_MONEY.cashDelivery:
        return isCashDeliveryActive || isCashDeliveryEditor;
      case PATH.RECEIVE_MONEY.EDC:
        return checkActive('EDC');
      // - end --------

      //* Expense Management
      // - composite --------
      case findPath(PATH.EXPENSE_MANAGEMENT.REIMBURSEMENT.path, path):
        return checkActive('REIMBURSEMENT');
      case findPath(PATH.EXPENSE_MANAGEMENT.BRIDGING_REPORT.path, path):
        return checkActive('VCC_USE_BRIDGE_FUND');
      case findPath(PATH.EXPENSE_MANAGEMENT.CORPORATE_CARD.path, path):
        return (
          checkActive('VIRTUAL_CREDIT_CARD') || checkActive('PHYSICAL_CARD')
        );
      case findPath(PATH.ACCOUNTS.userManagement, path):
        if (!isProdEnv) return true;
        if (!businessData.business_type) return false;
        return businessData.business_type === BusinessTypeValue.Individual
          ? isSubAccountActive
          : true;
      // Hide due to using newFlow
      case PATH.TRANSACTION_REPORT.accountStatement:
        // TODO: change with config newFlow
        return isAccountApproved && !isNewFlow;

      // Refund Report
      case PATH.TRANSACTION_REPORT.refundReport:
        return (
          isSOFActive &&
          (checkActive('BULK_DISBURSEMENT') ||
            checkActive('API_DISBURSE') ||
            checkActive('INTERNATIONAL_TRANSFER') ||
            checkActive('CLAIM_FUND') ||
            checkActive('API_INQUIRY'))
        );

      // - end --------

      //* ABSOLUTE TRUE
      case PATH.vaActivation:
      case PATH.ANALYTIC.paymentMethodDetail:
      case PATH.bcaActivation:
      case PATH.home:
      case PATH.root:
      case PATH.announcements:
      case PATH.helpAndDocumentation:
      case PATH.notifications:
      case PATH.walletRequest:
      case findPath(PATH.ACCOUNTS.path, path):
      case findPath(PATH.BOOK.path, path):
      case findPath(PATH.SETTINGS.path, path):
      case findPath(PATH.TRANSACTION_REPORT.path, path):
        return true;
      default:
        return false;
    }
  };

  const routeIsActive = (pathname = '') => {
    return (
      isIncludeToStaging(pathname) ||
      (isShowIfViewOnlyChildActive(pathname) &&
        isPathAccessible(pathname) &&
        isPathAccessibleByPermission(pathname))
    );
  };

  return {
    routeIsActive,
    isShowIfViewOnlyChildActive,
  };
};
